// Pulso shell — page header + content slot. The Pulso wordmark lives in the
// global PublicHeader (right of "Entrar"), so this component no longer
// renders its own logo. The wrapping `pulso-section` class scopes the brand
// color/typography overrides defined in `pulso-theme.css` to this subtree
// only — the rest of finzo.pt is unaffected.
//
// The topic strip has moved entirely into the index hero (the glossy
// frosted-segmented bar over the photo). This layout header now carries the
// page's editorial title; the in-hero title plate is gone so the photo can
// breathe behind the search bar + carousel.

import { useEffect } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Side-effect import: registers the `.pulso-section` token overrides + the
// rounded-sans heading family. CSS is inlined into the build, no FOUC.
import "./pulso-theme.css";

export const PulsoLayout = () => {
  const { t } = useTranslation();
  // Reset window scroll to the top on every PUSH navigation inside the
  // Pulso section. Without this, clicking a related-article link at the
  // bottom of an article lands the user at the bottom of the next article
  // (React Router does not manage scroll position for client-side nav, and
  // helmet-driven SSG only fixes the initial document load).
  //
  // We deliberately key off `useNavigationType` so POP (browser back /
  // forward) and REPLACE keep their natural scroll memory — only fresh
  // forward navigation jumps to the top, matching the user's mental model.
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType === "PUSH") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  // Only the Pulso index uses the section slogan as the page's H1. Topic and
  // article routes render their own H1 (topic name / article title), so the
  // slogan must downgrade to a paragraph to avoid multiple H1s on a page.
  const isPulsoIndex = pathname === "/pulso" || pathname === "/pulso/";
  const HeadingTag = isPulsoIndex ? "h1" : "p";

  return (
    <div className="pulso-section min-h-[calc(100vh-53px)]">
      {isPulsoIndex && (
        <header className="bg-background/80 backdrop-blur">
          <div className="mx-auto max-w-6xl px-4 pb-3 pt-8 text-center sm:pb-4 sm:pt-10">
            <HeadingTag className="text-balance font-serif text-3xl font-semibold leading-[1.1] tracking-tight text-foreground sm:text-4xl">
              {t("pulso.hero.title")}
            </HeadingTag>
          </div>
        </header>
      )}

      <div>
        <Outlet />
      </div>
    </div>
  );
};
